import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Resets scroll to top on every route change.
 *
 * Mobile browsers (especially iOS Safari) don't reliably honour
 * `window.scrollTo({ behavior: "instant" })` — the page can appear to "open
 * from the bottom" because the previous scroll position is retained until the
 * new content lays out. To fix that we:
 *   1. Disable browser scroll restoration so the OS doesn't fight us.
 *   2. Run BEFORE paint via useLayoutEffect to reset scroll before the new
 *      page becomes visible.
 *   3. Set every possible scroll surface (`window`, `documentElement`, `body`)
 *      because different mobile browsers expose scroll on different ones.
 *   4. Re-apply on the next animation frame in case a layout-shift happens
 *      right after mount (e.g. images loading, fonts swapping).
 */
const resetScroll = () => {
    if (typeof window === "undefined") return;
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
};

const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }
    }, []);

    useLayoutEffect(() => {
        resetScroll();
        // Re-apply after paint so late-loading content (images, fonts) can't
        // shift the viewport back down on mobile.
        const raf = requestAnimationFrame(resetScroll);
        return () => cancelAnimationFrame(raf);
    }, [pathname]);

    return null;
};

export default ScrollToTop;
